import React from "react";
import { Link } from "react-router-dom";
import { useContext } from "react";
import {
  FileText,
  Heart,
  Pill,
  AlertTriangle,
  Stethoscope,
} from "lucide-react";
import GlobalContext from "../context/GlobalContext";

export default function HistoryPage() {
  const { userInfo } = useContext(GlobalContext);

  const sections = [
    {
      title: "Current Conditions",
      icon: Heart,
      description: "Active medical conditions and ongoing treatments",
      content:
        "Hypertension (controlled with medication), Type 2 Diabetes (managed with diet and exercise), Seasonal allergies currently being treated with antihistamines.",
    },
    {
      title: "Past Conditions",
      icon: FileText,
      description: "Previously treated medical conditions",
      content:
        "Appendectomy (2019), Bronchitis (2022 - fully recovered), Minor concussion (2021 - no lasting effects), Gastroenteritis (2023 - resolved).",
    },
    {
      title: "Past Surgeries",
      icon: Stethoscope,
      description: "Surgical procedures and operations",
      content:
        "Laparoscopic appendectomy (March 2019) - successful with no complications. Wisdom tooth extraction (August 2020) - routine procedure, healed normally.",
    },
    {
      title: "Allergies",
      icon: AlertTriangle,
      description: "Known allergies and sensitivities",
      content:
        "Penicillin (severe reaction - rash and swelling), Shellfish (mild to moderate - digestive issues), Pollen (seasonal - nasal congestion and sneezing).",
    },
    {
      title: "Other Important Notes",
      icon: Pill,
      description: "Additional medical information",
      content:
        "Family history of heart disease and diabetes. Regular exercise routine (3x per week). Non-smoker, occasional alcohol consumption. Current medications: Metformin 500mg daily, Lisinopril 10mg daily.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 sm:px-6 md:px-8 py-4">
          <Link
            to="/"
            className="flex items-center gap-3 font-bold text-2xl text-slate-800 hover:text-green-700 transition-colors"
          >
            <div className="bg-green-100 p-2 rounded-xl">
              <img
                src="/assets/logo/logo.svg"
                alt="MedTag Logo"
                className="h-6 w-6"
              />
            </div>
            MedTag
          </Link>

          <nav className="flex items-center gap-3 sm:gap-6 text-sm">
            {!userInfo ? (
              <>
                <Link
                  to="/contact"
                  className="text-slate-600 hover:text-green-700 transition-colors font-medium"
                >
                  Contact
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:border-green-500 hover:text-green-700 transition-all font-medium"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md"
                >
                  Sign Up
                </Link>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-700 font-semibold text-sm">
                    {userInfo.first_name?.charAt(0)}
                  </span>
                </div>
                <span className="font-semibold text-slate-700 hidden sm:block">
                  {userInfo.first_name}
                </span>
              </div>
            )}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12">
        {/* Title Section */}
        <div className="text-center mb-10 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-3">
            MedTag <span className="text-slate-400 font-normal">Summary</span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-green-600 mx-auto mb-6"></div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-slate-200 max-w-md mx-auto">
            <p className="text-slate-600 mb-4 font-medium">Are you a Doctor?</p>
            <button className="bg-gradient-to-r from-green-600 to-green-700 text-white text-sm font-medium px-6 py-3 rounded-xl hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
              View past medical history
            </button>
          </div>
        </div>

        {/* Medical Sections */}
        <div className="grid gap-6 sm:gap-8">
          {sections.map((section, idx) => {
            const IconComponent = section.icon;
            return (
              <div key={idx} className="group">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300">
                  {/* Section Header */}
                  <div className="bg-gradient-to-r from-slate-50 to-green-50 px-6 sm:px-8 py-4 sm:py-5 border-b border-slate-100">
                    <div className="flex items-center gap-4">
                      <div className="bg-green-100 p-3 rounded-xl group-hover:bg-green-200 transition-colors">
                        <IconComponent className="w-5 h-5 text-green-700" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-1">
                          {section.title}
                        </h3>
                        <p className="text-sm text-slate-500 hidden sm:block">
                          {section.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Section Content */}
                  <div className="px-6 sm:px-8 py-6 sm:py-7">
                    <p className="text-slate-700 leading-relaxed text-sm sm:text-base">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
